import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { FileTextIcon, MailIcon, PrinterIcon, RotateCcwIcon, UserIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { fetchOrder } from '../adminApi';
import { useAdmin } from '../AdminContext';
import { PageHeader } from '../ui/PageHeader';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { MissingItem } from '../ui/MissingItem';
import { LoadFailed } from '../ui/LoadState';
import { Spinner } from '../../ui/Spinner';
import { btnDestructive, btnSecondary } from '../ui/styles';
import { paymentLabel, received } from '../inbox/inboxRules';
import { RefundForm } from './RefundForm';
import { CancelForm } from './CancelForm';
import { OrderTimeline } from './OrderTimeline';
import { FulfilmentPanel } from './FulfilmentPanel';
import { OrderMarginPanel } from './OrderMarginPanel';
import { creditNumber, printCreditNote, printOrderInvoice, printOrderPackingSlip } from './orderDocuments';
import { channelLabel, orderBadge, orderNumber, refundLabel, vatBreakdown } from './orderLabels';
import { money } from '../../core/format/currency';
import { cancelProblem, paymentMethodLabel, refundableCents } from '../../core/rules/revenue';
import { formatStreet } from '../../core/rules/contact';
import { trackingUrl } from '../../core/rules/tracking';
import { dayShort } from '../../core/format/dates';
import { groupByBundle } from '../../core/rules/orderBundles';
import type { AdminOrder } from '../../core/types/adminRequests';
import type { RequestOptions } from '../../core/types/backend';
type Action = 'refund' | 'cancel' | null;

/**
 * Eén bestelling: wat erin zat (met de btw zoals verkocht), het adres, de betaling, de tijdlijn en
 * de terugbetalingen. Terugbetalen en annuleren wachten altijd op de server (transactiescherm).
 */
export function OrderDetail() {
  const {
    id = ''
  } = useParams();
  // Vanuit de lijst: terug met dezelfde filters. Vanuit de inbox: terug naar de inbox.
  const {
    state
  } = useLocation() as {
    state: {
      from?: string;
      back?: {
        to: string;
        label: string;
      };
    } | null;
  };
  const back = state?.back ?? {
    to: `/be/bestellingen${state?.from ?? ''}`,
    label: 'Alle bestellingen'
  };
  const load = useCallback((opts: RequestOptions) => fetchOrder(id, opts), [id]);
  const {
    data: order,
    setData,
    status,
    reload,
    refresh
  } = useLatestLoad(load);
  const [action, setAction] = useState<Action>(null);
  if (status === 'loading' && !order) return <div role="status" className="flex items-center gap-2 py-16 text-[14px] text-muted" data-id="element-3494" data-mp="1tmax6x928wyl">
        <Spinner data-id="element-3495" data-mp="1to40q3odn12d" /> Bestelling laden…
      </div>;
  if (status === 'error' && !order) return <LoadFailed title="Deze bestelling kon niet laden" onRetry={reload} data-id="element-3496" data-mp="3o0brd4to0vsc" />;
  if (!order) return <MissingItem title="Bestelling niet gevonden" back={back} data-id="element-3497" data-mp="3jyqkq17q5dql" />;
  const done = (next: AdminOrder, message: string) => {
    setData(next);
    setAction(null);
    toast.success(message);
  };
  return <OrderView order={order} back={back} action={action} setAction={setAction} onRefunded={next => done(next, 'Terugbetaald. De klant krijgt een mail van Mollie.')} onCancelled={next => done(next, 'Bestelling geannuleerd')} onFailed={refresh} onFulfilled={refresh} data-id="element-3498" data-mp="1aq28fxm7gesb" />;
}
type ViewProps = {
  order: AdminOrder;
  back: {
    to: string;
    label: string;
  };
  action: Action;
  setAction: (a: Action) => void;
  onRefunded: (o: AdminOrder) => void;
  onCancelled: (o: AdminOrder) => void;
  onFailed: () => void;
  onFulfilled: () => void;
};
function OrderView({
  order: o,
  back,
  action,
  setAction,
  onRefunded,
  onCancelled,
  onFailed,
  onFulfilled
}: ViewProps) {
  const {
    site,
    index
  } = useAdmin();
  const badge = orderBadge(o);
  const paid = o.paymentStatus === 'betaald';
  const left = refundableCents(o);
  const canRefund = paid && left > 0 && !o.cancelledAt;
  const canCancel = cancelProblem(o) === null;
  const vat = vatBreakdown(o);
  const a = o.address;

  // Het formulier opent onder de regels; op de telefoon valt dat buiten beeld.
  const formRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (action) formRef.current?.scrollIntoView({
      block: 'nearest'
    });
  }, [action]);
  const invoice = async () => {
    if (!(await printOrderInvoice(o, site.salon, dayShort(o.placedAt.slice(0, 10))))) toast.error('Factuur openen lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  const slip = async () => {
    if (!(await printOrderPackingSlip(o, index.productById, site.salon, id => index.routineById(id)?.name))) toast.error('Pakbon afdrukken lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  return <div data-id="element-3499" data-mp="3cfzn50kyfo2h">
      <PageHeader title={o.number ? `Bestelling ${o.number}` : 'Bestelling zonder nummer'} description={`${o.customerName || o.email} · ${received(o.placedAt)} · ${channelLabel[o.channel]} · ${money(o.totalCents)}`} back={back} actions={<>
            <Badge tone={badge.tone} data-id="element-3501" data-mp="3w0hs1t8oqr83">{badge.label}</Badge>
            {paid && <button type="button" onClick={() => void invoice()} className={btnSecondary} data-id="element-3502" data-mp="3i071krbafeng">
                <FileTextIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3503" data-mp="3m229ienq4qa7" />
                Factuur
              </button>}
            {paid && !o.cancelledAt && <button type="button" onClick={() => void slip()} className={btnSecondary} data-id="element-3504" data-mp="3d2f8htywpmho">
                <PrinterIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3505" data-mp="2ydrg1wkcxsvy" />
                Pakbon
              </button>}
          </>} data-id="element-3500" data-mp="3cfhl1nbm2fmn" />

      {/* Art. 35a Wet OB: het btw-id hoort op elke factuur. Leeg = de factuur mist het, dus zeg het hier. */}
      {paid && !site.salon.vatId.trim() && <p role="note" className="mb-6 rounded-lg bg-sand px-4 py-3 text-[13px] text-ink" data-id="element-3506" data-mp="2uacrghics46w">
          Je btw-id staat nog niet in Instellingen › Salon & contact. Zonder btw-id is deze factuur niet volledig.{' '}
          <Link to="/be/instellingen?tab=salon" className="underline underline-offset-4" data-id="element-3507" data-mp="v3edwlyr0agu">
            Btw-id invullen
          </Link>
        </p>}

      <div className="grid gap-6 lg:grid-cols-3" data-id="element-3508" data-mp="m4kjn28w3w6g">
        <div className="space-y-6 lg:col-span-2" data-id="element-3509" data-mp="m5vm8yqlmxvz">
          <FulfilmentPanel order={o} onChanged={onFulfilled} data-id="element-3510" data-mp="1iujtd00s10qm" />
          <Panel title="Wat er besteld is" flush data-id="element-3511" data-mp="1irxl0y62ah7c">
            <table className="w-full text-[14px]" data-id="element-3512" data-mp="1dw9y6pf1y050">
              <caption className="sr-only" data-id="element-3513" data-mp="1dtdvkns9ena8">Bestelregels met btw</caption>
              <thead className="text-[12px] text-muted" data-id="element-3514" data-mp="19rsfktkfhsvt">
                <tr className="border-b border-line" data-id="element-3515" data-mp="14vbru0h55gj9">
                  <th scope="col" className="px-4 py-2 text-left font-normal sm:px-5" data-id="element-3516" data-mp="14wmxk5u5cyd0">Product</th>
                  <th scope="col" className="px-2 py-2 text-right font-normal" data-id="element-3517" data-mp="3uhrk0t29ljto">Aantal</th>
                  <th scope="col" className="hidden px-2 py-2 text-right font-normal sm:table-cell" data-id="element-3518" data-mp="3pjgcwq3ctnf5">Btw</th>
                  <th scope="col" className="px-4 py-2 text-right font-normal sm:px-5" data-id="element-3519" data-mp="250mb1ekdx4yd">Bedrag</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line" data-id="element-3520" data-mp="2e2vhntcj8e6h">
                {/* Een protocol als bundel onder één kopje: dan pakt Sarah het als set in. */}
                {groupByBundle(o.lines, l => l.bundleRoutineId).flatMap(g => [...(g.routineId ? [<tr key={`bundle-${g.routineId}`} className="bg-sand/60" data-id="element-3521" data-mp="2srrwc993gpg2">
                          <th scope="rowgroup" colSpan={4} className="px-4 py-2 text-left text-[12px] font-normal text-clay sm:px-5" data-id="element-3522" data-mp="2xrnsjmbjvcl2">
                            Protocol · {index.routineById(g.routineId)?.name ?? g.routineId} · {g.lines.length} producten
                          </th>
                        </tr>] : []), ...g.lines.map(l => <tr key={l.productId} data-id="element-3523" data-mp="oig8guu3h5mh">
                      <td className={`py-3 pr-4 text-ink sm:pr-5 ${g.routineId ? 'pl-7 sm:pl-8' : 'pl-4 sm:pl-5'}`} data-id="element-3524" data-mp="oh59d8blgm1p">
                        {l.name}
                        <span className="block text-[12px] text-muted sm:hidden" data-id="element-3525" data-mp="xarzoix18x3i">btw {l.vatRate}%</span>
                      </td>
                      <td className="px-2 py-3 text-right tabular-nums" data-id="element-3526" data-mp="tahkcfncoubz">{l.qty}</td>
                      <td className="hidden px-2 py-3 text-right tabular-nums text-muted sm:table-cell" data-id="element-3527" data-mp="5j2dc9kmpoue">{l.vatRate}%</td>
                      <td className="px-4 py-3 text-right tabular-nums text-ink sm:px-5" data-id="element-3528" data-mp="m2h0qdl00gh">{money(l.lineTotalCents)}</td>
                    </tr>)])}
              </tbody>
            </table>
            <dl className="space-y-1.5 border-t border-line px-4 py-4 text-[13px] sm:px-5" data-id="element-3529" data-mp="9k4ewx20qczj">
              <div className="flex justify-between gap-4" data-id="element-3530" data-mp="1pxujs60yp5a8">
                <dt className="text-muted" data-id="element-3531" data-mp="1l1dw1cxocsxo">Verzending (btw {o.shippingVatRate}%)</dt>
                <dd className="tabular-nums text-ink" data-id="element-3532" data-mp="1thuzeuefh63q">{o.shippingCents ? money(o.shippingCents) : 'Gratis'}</dd>
              </div>
              {vat.map(v => <div key={v.vatRate} className="flex justify-between gap-4" data-id="element-3533" data-mp="1xh3sdx1n8zy8">
                  <dt className="text-muted" data-id="element-3534" data-mp="1jl02gfxszdrk">Waarvan btw {v.vatRate}%</dt>
                  <dd className="tabular-nums text-muted" data-id="element-3535" data-mp="1ohqkv9x2ylg5">{money(v.vatCents)}</dd>
                </div>)}
              <div className="flex justify-between gap-4 pt-1.5 text-[15px]" data-id="element-3536" data-mp="2c9ff4vtgpjzq">
                <dt className="text-ink" data-id="element-3537" data-mp="2h7gzrl6jdrdb">Totaal</dt>
                <dd className="tabular-nums text-ink" data-id="element-3538" data-mp="2h90fnaoa60ek">{money(o.totalCents)}</dd>
              </div>
              {o.voucherCents > 0 && <div className="flex justify-between gap-4" data-id="element-3539" data-mp="27gmbycdn6jpr">
                  <dt className="text-muted" data-id="element-3540" data-mp="27f1i6oz5j4sy">Betaald met cadeaubon (gaat niet via Mollie terug)</dt>
                  <dd className="shrink-0 tabular-nums text-ink" data-id="element-3541" data-mp="2v55hwl97ykeo">{money(o.voucherCents)}</dd>
                </div>}
              {o.refunds.map(r => <div key={r.id} className="flex justify-between gap-4" data-id="element-3542" data-mp="1s20j65l7gu5f">
                  <dt className="min-w-0 text-muted" data-id="element-3543" data-mp="1x0twny0hsl5l">
                    {refundLabel(r)} {received(r.at)} · <span className="text-ink" data-id="element-3544" data-mp="1j1lwp2wi990c">{r.reason}</span>
                    {r.restocked && <span data-id="element-3545" data-mp="1nznelki732a8"> · terug op voorraad</span>}
                    {r.voucherRestoredCents ? <span data-id="element-3546" data-mp="1o1qf4ygz4dt7"> · {money(r.voucherRestoredCents)} terug op cadeaubon</span> : null}
                    <button type="button" onClick={() => void printCreditNote(o, r, site.salon).then(ok => {
                  if (!ok) toast.error('Creditnota openen lukte niet', {
                    description: 'Probeer het opnieuw of gebruik een andere browser.'
                  });
                })} className="ml-2 text-ink underline decoration-line underline-offset-4 hover:decoration-ink" data-id="element-3547" data-mp="198wqonjq4nce">
                      Creditnota {creditNumber(o, r)}
                    </button>
                  </dt>
                  <dd className="shrink-0 tabular-nums text-clay" data-id="element-3548" data-mp="6qlhop9mv47t">−{money(r.amountCents)}</dd>
                </div>)}
            </dl>
            {(o.gift || o.code) && <p className="border-t border-line px-4 py-3 text-[13px] text-muted sm:px-5" data-id="element-3549" data-mp="ll9me177lymd">
                {o.gift && <>
                    Cadeau erbij: <span className="text-ink" data-id="element-3550" data-mp="umzk1oroptsl">{o.gift}</span>
                  </>}
                {o.gift && o.code && ' · '}
                {o.code && <>
                    Code: <span className="text-ink" data-id="element-3551" data-mp="14dkinc7cyybi">{o.code}</span>
                  </>}
              </p>}
          </Panel>

          <OrderMarginPanel order={o} data-id="element-3552" data-mp="1deip0159rhx9" />

          <div ref={formRef} className="scroll-mb-24" data-id="element-3553" data-mp="1dfldy3j17aaj">
            {action === 'refund' ? <RefundForm order={o} onDone={onRefunded} onFailed={onFailed} onClose={() => setAction(null)} data-id="element-3554" data-mp="19e8au17bpl7b" /> : action === 'cancel' ? <CancelForm order={o} onDone={onCancelled} onFailed={onFailed} onClose={() => setAction(null)} data-id="element-3555" data-mp="1lfhmaxpjamhf" /> : (canRefund || canCancel) && <div className="flex flex-wrap items-center gap-2" data-id="element-3556" data-mp="1qd9qaq3gdz1z">
                  {canRefund && <button type="button" onClick={() => setAction('refund')} className={btnSecondary} data-id="element-3557" data-mp="1v7dwv88t452j">
                      <RotateCcwIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3558" data-mp="1z8z8yrdiofbo" />
                      Terugbetalen
                    </button>}
                  {canCancel && <button type="button" onClick={() => setAction('cancel')} className={`${btnDestructive} sm:ml-auto`} data-id="element-3559" data-mp="2e45bh4delw09">
                      <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3560" data-mp="erkuyvaamjdm" />
                      Bestelling annuleren
                    </button>}
                </div>}
          </div>
          {!canCancel && !o.cancelledAt && paid && o.fulfilment !== 'nieuw' && action === null && <p className="-mt-3 text-[12px] text-muted" data-id="element-3561" data-mp="2s8e2mi7nvxd4">Al ingepakt of verzonden: annuleren kan niet meer, terugbetalen wel.</p>}
        </div>

        <div className="space-y-6" data-id="element-3562" data-mp="2j9ivn3wsmj8r">
          <Panel title="Klant" data-id="element-3563" data-mp="2jakbdlor7twt">
            <p className="text-[14px] text-ink" data-id="element-3564" data-mp="2j768mcc6hiou">{o.customerName || '—'}</p>
            <p className="break-all text-[13px] text-muted" data-id="element-3565" data-mp="3fygryqk6yp81">{o.email}</p>
            <address className="mt-3 text-[13px] not-italic leading-snug text-ink" data-id="element-3566" data-mp="3byps7xcosyrd">
              {`${a.firstName} ${a.lastName}`.trim() || o.customerName}
              <br data-id="element-3567" data-mp="31zh43l4i6ecx" />
              {formatStreet(a)}
              <br data-id="element-3568" data-mp="31yyza03ryrte" />
              {a.postcode} {a.city}
              {a.country === 'BE' && ' · België'}
            </address>
            {o.skinNote && <p className="mt-3 rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-3569" data-mp="1qkli1e3j1amc">
                <span className="block text-[12px] text-muted" data-id="element-3570" data-mp="3eh9s3lsk76dz">Notitie voor jou</span>“{o.skinNote}”
              </p>}
            <div className="mt-4 flex flex-wrap gap-2" data-id="element-3571" data-mp="3j8rt29ul16yy">
              {o.customerId ? <Link to={`/be/klanten/${o.customerId}?tab=bestellingen`} className={btnSecondary} data-id="element-3572" data-mp="35h4kbajge5za">
                  <UserIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3573" data-mp="3aemxjd4i77d3" />
                  Dossier
                </Link> : <span className="text-[12px] text-muted" data-id="element-3574" data-mp="1xs3mn5ooxb5">Gastbestelling</span>}
              <a href={`mailto:${o.email}?subject=${encodeURIComponent(`Je bestelling ${orderNumber(o)}`)}`} className={btnSecondary} data-id="element-3575" data-mp="5z3tmtsgzkjv">
                <MailIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3576" data-mp="3t5ebyt3clnyc" />
                Mail
              </a>
            </div>
          </Panel>

          <Panel title="Betaling" data-id="element-3577" data-mp="2ctqurxi34cp7">
            <dl className="space-y-1.5 text-[13px]" data-id="element-3578" data-mp="2hra88v6yklg3">
              <div className="flex justify-between gap-4" data-id="element-3579" data-mp="2hs26yevuqzal">
                <dt className="text-muted" data-id="element-3580" data-mp="2a6gf87doeo7y">Status</dt>
                <dd className="text-ink" data-id="element-3581" data-mp="2a92n684hb5qp">{paymentLabel[o.paymentStatus]}</dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-3582" data-mp="25a9auj0xhks4">
                <dt className="text-muted" data-id="element-3583" data-mp="2j6uzr4itfzae">Methode</dt>
                <dd className="text-ink" data-id="element-3584" data-mp="2e8tjssgosg35">{paymentMethodLabel[o.paymentMethod]}</dd>
              </div>
              {o.refundedCents > 0 && <div className="flex justify-between gap-4" data-id="element-3585" data-mp="1qk1z0m5ff00e">
                  <dt className="text-muted" data-id="element-3586" data-mp="1mhx6jqqps9fc">Nog terug te betalen</dt>
                  <dd className="tabular-nums text-ink" data-id="element-3587" data-mp="1mfkhktmjgyss">{money(left)}</dd>
                </div>}
              {o.tracking && <div className="flex justify-between gap-4" data-id="element-3588" data-mp="1vhtsvlpmsw78">
                  <dt className="text-muted" data-id="element-3589" data-mp="1vgilzd0w38bw">Track & trace</dt>
                  <dd data-id="element-3590" data-mp="17rg6aaptfpk1">
                    <a href={trackingUrl(o.tracking)} target="_blank" rel="noopener noreferrer" className="text-ink underline underline-offset-2" data-id="element-3591" data-mp="17qoaozw7xrtr">
                      {o.tracking.carrier} {o.tracking.code}
                      <span className="sr-only" data-id="element-3592" data-mp="32u34zughglwt"> (opent in een nieuw tabblad)</span>
                    </a>
                  </dd>
                </div>}
            </dl>
          </Panel>

          <Panel title="Tijdlijn" data-id="element-3593" data-mp="3co35fj0tl2z7">
            <OrderTimeline events={o.timeline} data-id="element-3594" data-mp="3hlbodltslb1i" />
          </Panel>
        </div>
      </div>
    </div>;
}